import { ClaudeTool, Command, CommandId, SelfContainedCommand } from 'types/commands';
import { CommandHandler } from 'types/commands';
import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('COMMAND_REGISTRY', { enabled: false });

// Define the command registry state
interface CommandRegistryState {
  commands: Map<CommandId, CommandHandler>;
  commandDefinitions: Map<CommandId, Command | SelfContainedCommand>;
}

// Define the command registry store
interface CommandRegistryStore extends CommandRegistryState {
  registerCommand: (id: CommandId, handler: CommandHandler) => void;
  unregisterCommand: (id: CommandId) => void;
  executeCommand: <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void) => Promise<T | undefined>;
  registerCommandDefinition: (command: Command | SelfContainedCommand) => void;
  getCommandDefinition: (id: CommandId) => Command | SelfContainedCommand | undefined;
}

// Create the command registry store
export const useCommandRegistryStore = create<CommandRegistryStore>((set, get) => ({
  commands: new Map(),
  commandDefinitions: new Map(),

  registerCommand: (id: CommandId, handler: CommandHandler) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.set(id, handler);
      return { commands: newCommands };
    });
  },

  unregisterCommand: (id: CommandId) => {
    set((state) => {
      const newCommands = new Map(state.commands);
      newCommands.delete(id);
      return { commands: newCommands };
    });
  },

  executeCommand: async <T>(id: CommandId, params: any, context?: any, onProgress?: (progress: any) => void): Promise<T | undefined> => {
    let { commands, commandDefinitions } = get();

    // Primary lookup in the handler cache; fall back to commandDefinitions if the cache is stale
    let handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;

    // If not found, re-initialize the full registry and retry.
    // This handles partial registry states (e.g. only tool commands registered via HMR/lazy-load,
    // but initializeAllCommands never ran for this module instance).
    if (!handler) {
      const log = createFileLogger('COMMAND_REGISTRY');
      log.warn(`[CommandRegistry] Command "${id}" not found (registry has ${commandDefinitions.size} commands). Re-initializing...`);
      await initializeAllCommands();
      ({ commands, commandDefinitions } = get());
      handler = commands.get(id) ?? (commandDefinitions.get(id) as any)?.handler;
    }
    
    if (handler) {
      try {
        // Build the execution context, including the onProgress callback
        const executionContext = {
          ...(context || {}),
          onProgress
        };

        let result;
        
        // New standard: handler(params, context)
        // Self-contained commands and new legacy commands follow this
        if (handler.length >= 2) {
          result = await handler(params, executionContext);
        } else {
          // Legacy: handler({ ...params, context })
          const legacyParams = {
            ...params,
            context: executionContext
          };
          result = await handler(legacyParams);
        }

        return result;
      } catch (error) {
        log.error(`Error executing command ${id}:`, error);
        
        // Re-throw the error so it can be caught by the CommandExecutor
        throw error;
      }
    } else {
      log.warn(`Command ${id} not found`);
      
      // Throw an error instead of returning undefined
      throw new Error(`Command not found: ${id}`);
    }
  },

  registerCommandDefinition: (command: Command | SelfContainedCommand) => {
    // Attach a default formatResultPreview if missing, to ensure completed cards show a meaningful label
    const enhanced = { ...command } as any;
    if (typeof enhanced.formatResultPreview !== 'function') {
      enhanced.formatResultPreview = (result: any, toolInput: any) => {
        try {
          // Prefer StandardToolResult.message
          if (result && typeof result.message === 'string' && result.message.length > 0) {
            return result.message;
          }
          // Prefer explicit summaries on data
          if (result && result.data) {
            const data = result.data;
            if (typeof data.summary === 'string' && data.summary.length > 0) return data.summary;
            // Generic search-style summaries
            const arrays = [
              ['results', Array.isArray((data as any).results) ? (data as any).results.length : undefined],
              ['hits', Array.isArray((data as any).hits) ? (data as any).hits.length : undefined],
              ['nodes', Array.isArray((data as any).nodes) ? (data as any).nodes.length : undefined],
              ['items', Array.isArray((data as any).items) ? (data as any).items.length : undefined],
              ['matches', Array.isArray((data as any).matches) ? (data as any).matches.length : undefined],
            ].filter(([_, v]) => typeof v === 'number' && (v as number) >= 0) as Array<[string, number]>;
            if (arrays.length > 0) {
              const [label, count] = arrays[0];
              return `Found ${count} ${label}`;
            }
            if (typeof (data as any).total === 'number') return `Found ${(data as any).total} results`;
            if (typeof (data as any).count === 'number') return `Found ${(data as any).count} results`;
            // ✅ ENHANCED: Use enhanced message context if available
            if (data.messageContext) {
              const ctx = data.messageContext;
              if (typeof ctx.operationSummary === 'string') {
                const preview = ctx.contentPreview ? ` - ${ctx.contentPreview}` : '';
                return `${ctx.operationSummary}${preview}`;
              }
            }
            // Common fields across doc tools
            if (typeof data.replacedCount === 'number') return `Replaced ${data.replacedCount} node(s)`;
            if (typeof data.removedCount === 'number') return `Removed ${data.removedCount} node(s)`;
            if (Array.isArray(data.insertedNodeIds)) return `Inserted ${data.insertedNodeIds.length} node(s)`;
            if (typeof data.type === 'string') return `Completed ${data.type.replace(/^document_/, '').replace(/_/g, ' ')}`;
          }
          // Fallback to input-provided expand text
          if (toolInput && typeof toolInput.expand === 'string' && toolInput.expand.length > 0) {
            return toolInput.expand;
          }
        } catch {}
        return 'Completed';
      };
    }
    set((state) => {
      const newDefinitions = new Map(state.commandDefinitions);
      newDefinitions.set(enhanced.id, enhanced);
      return { commandDefinitions: newDefinitions };
    });
  },

  getCommandDefinition: (id: CommandId) => {
    const { commandDefinitions } = get();
    return commandDefinitions.get(id);
  },
}));

export const createToolsFromCommands = (commands: (Command | SelfContainedCommand)[]): ClaudeTool[] => {
  return commands.map(command => {
    // Handle both Command and SelfContainedCommand types
    const parameters = 'parameters' in command 
      ? command.parameters // Legacy Command
      : command.schema.parameters; // SelfContainedCommand

    return {
      name: command.id,
      description: command.description,
      input_schema: {
        type: "object",
        properties: Object.fromEntries(
          parameters.map((param: any) => {
            const propertyDef: any = {
              type: param.type,
              description: param.description
            };
            
            // Include default value if present (helps Claude understand optional parameters)
            if (param.defaultValue !== undefined) {
              propertyDef.default = param.defaultValue;
            }
            
            // For array types, include items schema if present (critical for Claude to understand array structure)
            if (param.type === 'array' && param.items) {
              propertyDef.items = param.items;
            }
            
            return [param.name, propertyDef];
          })
        ),
        required: parameters
          .filter((param: any) => param.required)
          .map((param: any) => param.name)
      }
    };
  });
};

export const registerCommands = (commands: (SelfContainedCommand | Command)[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  commands.forEach(command => {
    commandRegistry.registerCommand(command.id, command.handler);
    // Store the command directly - registry now supports both types
    commandRegistry.registerCommandDefinition(command);
  });
};

// NEW: Register self-contained commands
export const registerSelfContainedCommands = (commands: any[]) => {
  const commandRegistry = useCommandRegistryStore.getState();
  const registeredIds: string[] = [];
  commands.forEach(command => {
    // Self-contained commands have a different handler signature but the registry doesn't care
    commandRegistry.registerCommand(command.id, command.handler);
    commandRegistry.registerCommandDefinition(command);
    registeredIds.push(command.id);
  });
  
};

/** Runs one app's command import + registration in isolation: a failed/stale-chunk
 * import (e.g. a 404 mid-deploy) only drops that app's commands — it never rejects
 * the shared `Promise.allSettled` batch or blanks out the other ~60 apps. */
async function safeRegister(label: string, task: () => Promise<void>): Promise<void> {
  try {
    await task();
  } catch (error) {
    log.warn(`[CommandRegistry] Failed to load "${label}" commands (non-fatal — other apps unaffected):`, error);
  }
}

// Actual bulk-load implementation. Never call directly — always go through the
// exported, memoized `initializeAllCommands()` below so concurrent/repeat callers
// (GUILayout prefetch, executeCommand's self-heal fallback, ensureCommandsInitialized
// call sites throughout SpaceChat) share one in-flight load instead of each kicking
// off their own ~60-import batch.
async function runCommandInitialization(): Promise<void> {
  log.info('[CommandRegistry] Starting command initialization...');

  // Clear existing commands via set() so Zustand state is consistent
  useCommandRegistryStore.setState({
    commands: new Map(),
    commandDefinitions: new Map(),
  });

  // Each app's commands are imported and registered independently. None of these
  // are in the static import graph, so their transitive deps (prosemirror, yjs,
  // pdf, etc.) stay out of the critical page-load path and are fetched only when
  // this function runs. Running them via Promise.allSettled (rather than
  // Promise.all + destructure-everything-then-register) means: (1) a single
  // failing/stale chunk only drops that one app's commands instead of rejecting
  // the whole batch, and (2) each app becomes usable the moment its own import
  // resolves instead of waiting on the slowest of ~60 fetches.
  await Promise.allSettled([
    safeRegister('spaceUI', async () => {
      const {
        spaceLifecycleCommands,
        windowManagementCommands,
        panelVisibilityCommands,
        sidebarTreeCommands,
        workflowUtilityCommands,
        driveNavigationCommands,
        codebaseWorkspaceCommands,
        setupCodebaseWorkspaceEventListener,
        codebaseProvisionCommands,
        codebaseFromUrlCommands,
        webDocumentFromUrlCommands,
        switchModeCommand,
        physicianWidgetCommands,
        medicationTableCommands,
        uiSurfaceCommands,
      } = await import('../systemCommands/spaceUI');
      registerSelfContainedCommands(spaceLifecycleCommands);
      registerSelfContainedCommands(windowManagementCommands);
      registerSelfContainedCommands(panelVisibilityCommands);
      registerSelfContainedCommands(sidebarTreeCommands);
      registerSelfContainedCommands(workflowUtilityCommands);
      registerSelfContainedCommands(driveNavigationCommands);
      registerSelfContainedCommands(codebaseWorkspaceCommands);
      setupCodebaseWorkspaceEventListener();
      registerSelfContainedCommands(codebaseProvisionCommands);
      registerSelfContainedCommands(codebaseFromUrlCommands);
      registerSelfContainedCommands(webDocumentFromUrlCommands);
      registerSelfContainedCommands([switchModeCommand]);
      registerSelfContainedCommands(physicianWidgetCommands);
      registerSelfContainedCommands(medicationTableCommands);
      registerSelfContainedCommands(uiSurfaceCommands);
    }),

    safeRegister('billing', async () => {
      const { billingCommands } = await import('../systemCommands/billing/billingCommands');
      registerSelfContainedCommands(billingCommands);
    }),

    safeRegister('entitySystem', async () => {
      const { entityCommands } = await import('../systemCommands/entitySystem');
      registerSelfContainedCommands(entityCommands);
    }),

    safeRegister('docsApp', async () => {
      const { commands: docsCommands } = await import('../apps/DocsApp/store/commands');
      log.info('[CommandRegistry] Got active docs commands:', {
        count: docsCommands.length,
        ids: docsCommands.map(c => c.id),
        yjsCount: docsCommands.filter(c => c.id.startsWith('yjs_')).length,
      });
      registerCommands(docsCommands);
    }),

    safeRegister('docsAppV5', async () => {
      const { markdownDocCommands: v5DocsCommands } = await import('../apps/DocsApp/commands/v5');
      registerSelfContainedCommands(v5DocsCommands);
    }),

    safeRegister('pdfium', async () => {
      const { commands: pdfiumCommands } = await import('../apps/PDFiumApp/commands');
      registerCommands(pdfiumCommands);
    }),

    safeRegister('bank', async () => {
      const { commands: bankCommands } = await import('../apps/BankApp/store/commands');
      registerCommands(bankCommands || []);
    }),

    safeRegister('browser', async () => {
      const { browserCommands } = await import('../apps/BrowserApp/commands');
      registerCommands(browserCommands);
    }),

    safeRegister('userInteraction', async () => {
      const { userInteractionCommands } = await import('../systemCommands/userInteraction');
      registerSelfContainedCommands(userInteractionCommands);
    }),

    safeRegister('universalCommands', async () => {
      const { UNIVERSAL_COMMANDS } = await import('../apps/UniversalCommands');
      registerSelfContainedCommands(UNIVERSAL_COMMANDS);
      log.info('[CommandRegistry] ✅ Registered Universal Commands:', {
        count: UNIVERSAL_COMMANDS.length,
        ids: UNIVERSAL_COMMANDS.map(c => c.id),
      });
    }),

    safeRegister('writersDashboardCreate', async () => {
      const { writersDashboardCreateCommand } = await import('../apps/WritersDashboardApp_v2/commands/writers_dashboard_create');
      registerSelfContainedCommands([writersDashboardCreateCommand]);
    }),

    safeRegister('dashboardCreate', async () => {
      const { dashboardCreateCommand } = await import('../apps/DashboardApp/commands/dashboard_create');
      registerSelfContainedCommands([dashboardCreateCommand]);
    }),

    safeRegister('manuscriptReviewReportCreate', async () => {
      const { manuscriptReviewReportCreateCommand } = await import('../apps/ManuscriptReviewReportApp_v2/commands/manuscript_review_report_create');
      registerSelfContainedCommands([manuscriptReviewReportCreateCommand]);
    }),

    safeRegister('bookProjectCreate', async () => {
      const { bookProjectCreateCommand } = await import('../apps/BookFormatterApp_v2/commands/book_project_create');
      registerSelfContainedCommands([bookProjectCreateCommand]);
    }),

    safeRegister('bookWrite', async () => {
      const { bookWriteCommand } = await import('../apps/BookFormatterApp_v2/commands/book_write');
      registerSelfContainedCommands([bookWriteCommand]);
    }),

    safeRegister('listenApp', async () => {
      const { listenAppCommands } = await import('../apps/ListenApp/commands');
      registerSelfContainedCommands(listenAppCommands);
    }),

    safeRegister('libraryApp', async () => {
      const { libraryCommands } = await import('../apps/LibraryApp/commands');
      registerSelfContainedCommands(libraryCommands);
    }),

    safeRegister('tasksApp', async () => {
      const { tasksCommands } = await import('../apps/TasksApp/store/commands');
      registerSelfContainedCommands(tasksCommands);
    }),

    safeRegister('templateEntityKit', async () => {
      // Shared read/update tools + one generated `<type>_create` command per
      // registered schema (Equipment, Character, and any future generic
      // template-entity type) — supersedes the old character-only command set.
      const { TEMPLATE_ENTITY_KIT_COMMANDS } = await import('../apps/TemplateEntityKit/commands');
      registerSelfContainedCommands(TEMPLATE_ENTITY_KIT_COMMANDS);
    }),

    safeRegister('entityEmbeds', async () => {
      // Registers no commands — a pure side-effect import that populates the
      // generic entityEmbedRegistry (gui/src/components/EntityEmbed) so any
      // surface embedding entities (e.g. NotesApp_v2's `entity_embed` node)
      // resolves a rich view for SocialPost as soon as the app boots, without
      // requiring Social Studio itself to have ever been opened. Add one
      // import per app that registers an embed here.
      await import('../apps/SocialStudioApp/embed/registerSocialStudioEmbed');
    }),

    safeRegister('notesMarkdown', async () => {
      const { notesCommands } = await import('../apps/NotesMarkdownApp/commands');
      registerSelfContainedCommands(notesCommands);
    }),

    safeRegister('database', async () => {
      const { DATABASE_COMMANDS } = await import('../apps/DatabaseApp/commands');
      registerSelfContainedCommands(DATABASE_COMMANDS);
    }),

    safeRegister('videoStudio', async () => {
      const { VIDEO_STUDIO_PROJECT_COMMANDS, VIDEO_STUDIO_COMMANDS } = await import('../apps/VideoStudioApp/commands');
      // Project creation + thin video_* tool wrappers.
      registerSelfContainedCommands(VIDEO_STUDIO_PROJECT_COMMANDS);
      registerSelfContainedCommands(VIDEO_STUDIO_COMMANDS);
    }),

    safeRegister('diagram', async () => {
      const { DIAGRAM_COMMANDS } = await import('../apps/DiagramApp/commands');
      registerSelfContainedCommands(DIAGRAM_COMMANDS);
    }),

    safeRegister('chart', async () => {
      const { CHART_COMMANDS } = await import('../apps/ChartApp/commands');
      registerSelfContainedCommands(CHART_COMMANDS);
    }),

    safeRegister('medicationTable', async () => {
      const { MEDTABLE_COMMANDS } = await import('../apps/widgets/physicians/MedicationTableApp/commands');
      registerSelfContainedCommands(MEDTABLE_COMMANDS);
    }),

    safeRegister('skillWizard', async () => {
      const { SKILLWIZARD_COMMANDS } = await import('../apps/SkillWizardApp/commands');
      registerSelfContainedCommands(SKILLWIZARD_COMMANDS);
    }),

    safeRegister('htmlCanvas', async () => {
      const { HTMLCANVAS_COMMANDS } = await import('../apps/HtmlCanvasApp/commands');
      registerSelfContainedCommands(HTMLCANVAS_COMMANDS);
    }),

    safeRegister('sheets', async () => {
      const { SHEETS_COMMANDS } = await import('../apps/SheetsApp_v2/commands');
      registerSelfContainedCommands(SHEETS_COMMANDS);
    }),

    safeRegister('presentations', async () => {
      const { PRESENTATIONS_COMMANDS } = await import('../apps/PresentationsApp_v2/commands');
      registerSelfContainedCommands(PRESENTATIONS_COMMANDS);
    }),

    safeRegister('googleDrive', async () => {
      const { googleDriveCommands } = await import('../apps/GoogleDriveApp/commands');
      registerSelfContainedCommands(googleDriveCommands);
    }),

    safeRegister('wordPress', async () => {
      const { wordPressCommands } = await import('../apps/WordPressApp/commands');
      registerSelfContainedCommands(wordPressCommands);
    }),

    safeRegister('executionLocal', async () => {
      const { toolCommands } = await import('../systemCommands/execution/local');
      registerSelfContainedCommands(toolCommands);
    }),

    safeRegister('newWindow', async () => {
      const { newWindowCommands } = await import('../apps/NewWindowApp/commands');
      registerSelfContainedCommands(newWindowCommands);
    }),

    safeRegister('openDialog', async () => {
      const { openDialogCommands } = await import('../commands/openDialog');
      registerSelfContainedCommands(openDialogCommands);
    }),

    safeRegister('entityPicker', async () => {
      const { entityPickerCommands } = await import('../commands/entityPicker');
      registerSelfContainedCommands(entityPickerCommands);
    }),

    safeRegister('canvas', async () => {
      const { canvasCommands } = await import('../apps/CanvasApp/commands');
      registerSelfContainedCommands(canvasCommands);
    }),

    safeRegister('integrations', async () => {
      const {
        apiConnectCommand,
        publicApiQueryCommand,
        enableAPIConnectionCommand,
        disableAPIConnectionCommand,
        apiConnectionReadCommand,
      } = await import('../systemCommands/integrations');
      registerSelfContainedCommands([
        apiConnectCommand,
        publicApiQueryCommand,
        apiConnectionReadCommand,
        enableAPIConnectionCommand,
        disableAPIConnectionCommand,
      ]);
    }),

    safeRegister('apiConnectionApp', async () => {
      const { apiConnectionCommands } = await import('../apps/APIConnectionApp/commands');
      registerSelfContainedCommands(apiConnectionCommands);
      log.info('[CommandRegistry] ✅ API Connection app commands registered:', {
        appCommands: apiConnectionCommands.map(c => c.id),
      });
    }),

    safeRegister('computeSession', async () => {
      const { computeSessionCommand } = await import('../systemCommands/execution/cloud/computeSession');
      registerSelfContainedCommands([computeSessionCommand]);
      log.info('[CommandRegistry] ✅ Compute Session command registered:', {
        computeSession: 'compute_session',
      });
    }),

    safeRegister('mail', async () => {
      const { mailAppCommands } = await import('../apps/MailApp/commands');
      registerSelfContainedCommands(mailAppCommands);
    }),

    safeRegister('automations', async () => {
      const { AUTOMATIONS_COMMANDS } = await import('../apps/AutomationsApp/commands/v1');
      registerSelfContainedCommands(AUTOMATIONS_COMMANDS);
    }),

    safeRegister('plan', async () => {
      const { planCreateCommand } = await import('../apps/PlanApp/commands/v1');
      registerSelfContainedCommands([planCreateCommand]);
    }),

    safeRegister('todoWrite', async () => {
      const { todoWriteCommand, todoReadCommand, planUpdateCommand } = await import('../systemCommands/agentTools/todo_write');
      registerSelfContainedCommands([todoWriteCommand, todoReadCommand, planUpdateCommand]);
    }),

    safeRegister('page', async () => {
      const { pageCommands } = await import('../apps/PageApp_v2/commands/v1');
      registerSelfContainedCommands(pageCommands);
    }),

    safeRegister('transcript', async () => {
      const { transcriptCreateCommand } = await import('../apps/TranscriptApp/commands/v1/transcript_create');
      registerSelfContainedCommands([transcriptCreateCommand]);
    }),

    safeRegister('contacts', async () => {
      const { contactsCommands } = await import('../apps/ContactsApp/commands/v1');
      registerSelfContainedCommands(contactsCommands);
    }),

    safeRegister('appBuilder', async () => {
      const { appBuilderCommands } = await import('../apps/AppBuilderApp/commands');
      registerSelfContainedCommands(appBuilderCommands);
      log.info('[CommandRegistry] ✅ App Builder commands registered:', {
        count: appBuilderCommands.length,
        ids: appBuilderCommands.map(c => c.id),
      });
    }),

    safeRegister('rules', async () => {
      const { rulesCommands } = await import('../apps/RulesApp_v2/commands');
      registerSelfContainedCommands(rulesCommands);
    }),

    safeRegister('prompts', async () => {
      const { promptsCommands } = await import('../apps/PromptsApp_v2/commands');
      registerSelfContainedCommands(promptsCommands);
    }),

    safeRegister('memory', async () => {
      const { memoryCommands } = await import('../apps/MemoryApp_v2/commands');
      registerSelfContainedCommands(memoryCommands);
    }),

    safeRegister('skills', async () => {
      const { skillCommands } = await import('../apps/SkillsApp_v2/commands');
      registerSelfContainedCommands(skillCommands);
    }),

    safeRegister('markdownAppV2', async () => {
      const { markdownDocCommands } = await import('../apps/MarkdownApp_v2/commands');
      registerSelfContainedCommands(markdownDocCommands);
    }),

    safeRegister('inbox', async () => {
      const { inboxCommands } = await import('../apps/InboxApp/commands');
      registerSelfContainedCommands(inboxCommands);
    }),

    safeRegister('shop', async () => {
      const { shopCommands } = await import('../commands/shop');
      registerSelfContainedCommands(shopCommands);
    }),

    safeRegister('passwordVault', async () => {
      const { passwordVaultCommands } = await import('../apps/PasswordVaultApp/commands');
      registerSelfContainedCommands(passwordVaultCommands);
    }),

    safeRegister('docSystem', async () => {
      const { DOC_SYSTEM_COMMANDS } = await import('../systemCommands/docSystem');
      registerSelfContainedCommands(DOC_SYSTEM_COMMANDS);
    }),

    safeRegister('canvasSystem', async () => {
      const { CANVAS_SYSTEM_COMMANDS } = await import('../systemCommands/canvasSystem');
      registerSelfContainedCommands(CANVAS_SYSTEM_COMMANDS);
    }),

    safeRegister('entityDownload', async () => {
      const { ENTITY_DOWNLOAD_COMMANDS } = await import('../systemCommands/entitySystem/entity_download');
      registerSelfContainedCommands(ENTITY_DOWNLOAD_COMMANDS);
    }),

    safeRegister('imageStudio', async () => {
      // Registers the Image Studio agent-broadcast handlers (image_studio_progress / _result).
      // Must be global so an agent-side tool can deliver results even before any
      // ImageStudio window is mounted, and must NOT be re-registered per-mount.
      const { IMAGE_STUDIO_COMMANDS } = await import('../apps/ImageStudioApp/commands/imageStudioCommands');
      registerSelfContainedCommands(IMAGE_STUDIO_COMMANDS);
    }),

    safeRegister('socialStudio', async () => {
      const { SOCIAL_STUDIO_COMMANDS } = await import('../apps/SocialStudioApp/commands/socialStudioCommands');
      registerSelfContainedCommands(SOCIAL_STUDIO_COMMANDS);
    }),

    safeRegister('aiCommands', async () => {
      const { aiCommands } = await import('./aiCommands');
      registerSelfContainedCommands(aiCommands);
    }),

    safeRegister('mediaCommands', async () => {
      // Must be inside command initialization so media commands (media_speak,
      // window_action_poll) survive any re-initialization of the full command map
      // — previously they were mount-time-only and would disappear if the
      // registry was re-initialized after Space.tsx unmounted.
      const { registerMediaCommandsInRegistry } = await import('../services/mediaCommandService');
      registerMediaCommandsInRegistry(useCommandRegistryStore.getState());
    }),
  ]);

  log.info('[CommandRegistry] ✅ Initialized all commands:', {
    total: useCommandRegistryStore.getState().commandDefinitions.size,
  });
}

// Concurrent/repeat callers (GUILayout's mount-time prefetch, executeCommand's
// self-heal fallback, and every ensureCommandsInitialized() call site) share this
// single in-flight load instead of each triggering their own ~60-import batch.
let commandsInitPromise: Promise<void> | null = null;

/**
 * Initialize (or re-initialize) the full command registry. Safe to call from
 * multiple places concurrently — they all await the same load. Once a load
 * settles, the next call starts a fresh one (used by executeCommand's fallback
 * to force a full reload when a specific command is unexpectedly missing).
 */
export const initializeAllCommands = (): Promise<void> => {
  if (!commandsInitPromise) {
    commandsInitPromise = runCommandInitialization().finally(() => {
      commandsInitPromise = null;
    });
  }
  return commandsInitPromise;
};

/**
 * Registry-First Pattern: Get commands for specific contexts
 * This is the SINGLE source of truth for all command access
 */
export const getCommandsForContext = (context: 'api' | 'tools' | 'ui' | 'all' = 'all'): (Command | SelfContainedCommand)[] => {
  const registry = useCommandRegistryStore.getState();
  const allCommands = Array.from(registry.commandDefinitions.values());
  
  // Context-specific filtering
  switch (context) {
    case 'api':
      // For backend API - ensure no duplicates, include all essential commands
      return allCommands.filter(() => {
        // Include all commands but ensure docs commands are properly filtered
        return true;
      });
      
    case 'tools':
      // For tool discovery - include commands that should be exposed to AI
      return allCommands.filter(cmd => {
        // Exclude internal/debug commands if needed
        return !cmd.id.includes('debug') && !cmd.id.includes('internal');
      });
      
    case 'ui':
      // For UI contexts - include commands for contextual tool selection
      return allCommands;
      
    case 'all':
    default:
      return allCommands;
  }
};

/**
 * Get commands by app type for contextual filtering
 */
export const getCommandsByApp = (appType: string): (Command | SelfContainedCommand)[] => {
  const allCommands = getCommandsForContext('all');
  
  // Filter commands by app prefix or app-specific logic
  const filtered = allCommands.filter(cmd => {
    if (appType === 'docs' || appType === 'doc') {
      return cmd.id.startsWith('docs_') || cmd.id.startsWith('yjs_') || cmd.id.startsWith('doc_');
    }
    if (appType === 'notes' || appType === 'note') {
      return cmd.id.startsWith('notes_');
    }
    if (appType === 'pdf' || appType === 'pdfium') {
      return cmd.id.startsWith('pdf_') || cmd.id.startsWith('pdfium_');
    }
    if (appType === 'bank') {
      return cmd.id.startsWith('bank_');
    }
    if (appType === 'google-drive') {
      return cmd.id.startsWith('google_');
    }
    if (appType === 'tasks') {
      return cmd.id.startsWith('task_') || cmd.id.startsWith('tasks_');
    }
    if (appType === 'book') {
      return cmd.id.startsWith('book_') || cmd.id === 'entity_read';
    }
    // ✅ UNIVERSAL: Commands that work with ANY v2 entity
    if (appType === 'universal') {
      return cmd.id.startsWith('entity_');
    }
    return false;
  });
  
  if (appType === 'docs' || appType === 'doc') {
    log.debug('[CommandRegistry][getCommandsByApp] Docs commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      docsCommands: filtered.length,
      docsCommandIds: filtered.map(c => c.id),
      yjsCommands: filtered.filter(c => c.id.startsWith('yjs_')).length,
      legacyCommands: filtered.filter(c => c.id.startsWith('docs_')).length,
      commands4: filtered.filter(c => c.id.startsWith('doc_')).length
    });
  }
  
  if (appType === 'google-drive') {
    log.log('[google tools feature] Google Drive commands filtered:', {
      appType,
      totalCommands: allCommands.length,
      googleDriveCommands: filtered.length,
      commandIds: filtered.map(c => c.id)
    });
  }
  
  return filtered;
};

/**
 * Ensure commands are initialized before accessing them. Cheap no-op once the
 * registry is populated; otherwise delegates to the memoized `initializeAllCommands()`,
 * so concurrent callers (e.g. GUILayout's prefetch racing a Cmd-K open) never
 * trigger duplicate ~60-import batches.
 */
export const ensureCommandsInitialized = async (): Promise<void> => {
  const registry = useCommandRegistryStore.getState();
  if (registry.commandDefinitions.size === 0) {
    await initializeAllCommands();
  }
};

/** Convert JSON Schema properties to the SelfContainedCommand parameter format. */
function buildParametersFromSchema(inputSchema: Record<string, any>): any[] {
  const properties = inputSchema?.properties ?? {};
  const required: string[] = inputSchema?.required ?? [];

  return Object.entries(properties).map(([name, def]: [string, any]) => ({
    name,
    type: def.type ?? 'string',
    description: def.description ?? '',
    required: required.includes(name),
    enum: def.enum,
    defaultValue: def.default,
  }));
}

/**
 * Register commands from installed App Bundles.
 * Called asynchronously after initializeAllCommands() — non-fatal if it fails.
 * Bundle commands delegate to callToolRuntime() just like first-party commands.
 * Built-in commands always win name conflicts.
 */
export const registerBundleCommands = (bundles: Array<{ bundleId: string; bundleName: string; tools: Array<{ name: string; description: string; inputSchema: Record<string, any>; handlerUrl: string }> }>): void => {
  const registry = useCommandRegistryStore.getState();
  let registered = 0;
  let skipped = 0;

  for (const bundle of bundles) {
    for (const tool of bundle.tools) {
      // Built-in wins on conflict
      if (registry.commands.has(tool.name as any)) {
        log.warn(`[CommandRegistry] ⚠️ bundle command "${tool.name}" conflicts with built-in — skipping`);
        skipped++;
        continue;
      }

      const command: any = {
        id: tool.name,
        description: tool.description,
        schema: {
          parameters: buildParametersFromSchema(tool.inputSchema),
        },
        handler: async (params: any, context: any) => {
          const { callToolRuntime } = await import('../utils/callToolRuntime');
          return callToolRuntime(tool.name, params, context);
        },
      };

      registry.registerCommand(tool.name as any, command.handler);
      registry.registerCommandDefinition(command);
      registered++;
    }
  }

  if (registered > 0 || skipped > 0) {
    log.info(`[CommandRegistry] registered ${registered} bundle commands from ${bundles.length} installed bundles`, {
      skipped,
    });
  }
};
